/**
 * `/stock-list/purchase`: the Stock List order form (`components/StockList/Purchase.tsx`, which
 * holds the flow and the pricing rules), on the Ryerson build only. The landing page's "Order
 * Now" button links here with `?qty=<n>`, and PayPal sends a buyer who cancels back here
 * (`web-payments-api/application/src/enums/paypal.enum.ts` → `EPaypalRedirectUrl`), so the path
 * must not change.
 *
 * Neither this page nor `Purchase.tsx` renders `components/Shared/model/MetaHead.tsx`, so the
 * page has no title, description or canonical link of its own. It isn't in the site search
 * index (`scripts/staticSearch.ts`).
 */
import React from "react";
import Purchase, { FormContent } from "@components/StockList/Purchase";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { useApplication } from "@ryerson/frontend.application";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";

/**
 * The order form's copy. `shipping` is the only place the buyer is told the Stock List ships to
 * US addresses only and not to a PO box. The `fr` and `es` entries repeat the English text.
 */
export const PurchaseContentHero: LanguageContent<FormContent> = {
	en: {
		requiredDirections: "Fields with * are required.",
		title: "Ryerson Stock List",
		shipping:
			"Please enter your information below. When finished, click ‘Complete Purchase’ in order to secure your purchase using PayPal. Please note that currently the Ryerson Stock List can only be delivered to U.S. addresses and cannot be sent to a PO box address.",
		firstNameLabel: "First Name",
		lastNameLabel: "Last Name",
		address1Label: "Address 1",
		address2Label: "Address 2",
		cityLabel: "City",
		stateLabel: "State",
		zipLabel: "Zipcode",
		emailLabel: "Email",
		phoneLabel: "Phone",
		quantityLabel: "Quantity",
		orderSummaryLabel: "Order Summary",
		buttonLabel: "Complete Purchase",
		priceLabel: "Extended Price",
		totalLabel: "Total",
		modalMessageSuccess: "Thank you for your order. Someone will be in contact with you soon.",
		modalMessageError: "Something went wrong. Please try again later.",
	},
	fr: {
		requiredDirections: "Fields with * are required",
		title: "Ryerson Stock List",
		shipping:
			"Please enter your information below. When finished, click ‘Complete Purchase’ in order to secure your purchase using PayPal. Please note that currently the Ryerson Stock List can only be delivered to U.S. addresses and cannot be sent to a PO box address.",
		firstNameLabel: "First Name",
		lastNameLabel: "Last Name",
		address1Label: "Address 1",
		address2Label: "Address 2",
		cityLabel: "City",
		stateLabel: "State",
		zipLabel: "Zipcode",
		priceLabel: "Extended Price",
		emailLabel: "Email",
		phoneLabel: "Phone",
		quantityLabel: "Quantity",
		orderSummaryLabel: "Order Summary",
		totalLabel: "Total",
		buttonLabel: "Complete Purchase",
		modalMessageSuccess: "Thank you for your order. Someone will be in contact with you soon.",
		modalMessageError: "Something went wrong. Please try again later.",
	},
	es: {
		requiredDirections: "Fields with * are required",
		title: "Ryerson Stock List",
		shipping:
			"Please enter your information below. When finished, click ‘Complete Purchase’ in order to secure your purchase using PayPal. Please note that currently the Ryerson Stock List can only be delivered to U.S. addresses and cannot be sent to a PO box address.",
		firstNameLabel: "First Name",
		lastNameLabel: "Last Name",
		address1Label: "Address 1",
		address2Label: "Address 2",
		cityLabel: "City",
		stateLabel: "State",
		zipLabel: "Zipcode",
		orderSummaryLabel: "Order Summary",
		priceLabel: "Extended Price",
		emailLabel: "Email",
		phoneLabel: "Phone",
		quantityLabel: "Quantity",
		totalLabel: "Total",
		buttonLabel: "Complete Purchase",
		modalMessageSuccess: "Thank you for your order. Someone will be in contact with you soon.",
		modalMessageError: "Something went wrong. Please try again later.",
	},
};

/**
 * Builds the page on the Ryerson build only (the Stock List isn't sold on any other brand) and
 * fetches the Contentful header, locations and footer data for `HeaderLayout`.
 */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	return await allContentfulHeaderCalls();
};

/** The `/stock-list/purchase` page; see the file header. */
const Page: React.FC<HeaderPageProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<Purchase content={PurchaseContentHero[language]} />
		</HeaderLayout>
	);
};

export default Page;
